import _ from 'lodash'

/**
 * Groups a flat list of plans by their clientId.
 * @param {Array<Object>} plans - Plans to group (each must have a clientId field).
 * @returns {Object<string, Array<Object>>} Plans grouped by clientId.
 */
export const groupPlansByClientId = (plans) =>
  [...plans].reduce(
    (result, item) => ({
      ...result,
      [item.clientId]: [...(result[item.clientId] || []), item]
    }),
    {}
  )

/**
 * Finds the currently selected option in a dropdown options list.
 * @param {Array<Object>} options - Dropdown options, each optionally flagged with isSelected.
 * @returns {Object|undefined} The selected option, if any.
 */
export const findSelectedOption = (options) =>
  options?.find((opt) => opt.isSelected)

/**
 * Filters JV and HRP plans down to those that carry a networkAffiliation
 * pointing at one of the currently available network options.
 * @param {Array<Object>} jvPlans - Javelina plans.
 * @param {Array<Object>} hrpPlans - HRP plans.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @returns {Array<Object>} Plans (JV + HRP) that are affiliated to a known network.
 */
export const getPlansWithNetworkAffiliation = (jvPlans, hrpPlans, networkOptions) =>
  [...jvPlans, ...hrpPlans].filter((p) => {
    if (!p.networkAffiliation) return false
    return networkOptions
      .map((netOpt) => netOpt.value)
      .includes(p.networkAffiliation.uuid)
  })

/**
 * Builds the "client ID" dropdown option list for a given plan type, annotating each
 * plan's networkAffiliationOptions with the currently selected network (if any).
 *
 * Note: mutates the netOpt entries of networkOptions and the p.networkAffiliationOptions
 * field of the plans found in plansGroupedByClientId, matching the original component behavior.
 *
 * @param {Object<string, Array<Object>>} plansGroupedByClientId - Plans grouped by clientId.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @param {string} currentClientName - The clientName of the client currently being edited (from route params).
 * @param {string} selectedClientId - The clientId currently selected in the tab.
 * @returns {Array<Object>} Client dropdown options, including a leading "NONE" entry.
 */
export const buildClientOptionsList = (plansGroupedByClientId, networkOptions, currentClientName, selectedClientId) => {
  const options = Object.keys(plansGroupedByClientId)
    .map((clientId) => ({
      title: clientId,
      value: clientId,
      plans: plansGroupedByClientId[clientId]
        .map((p) => {
          const networkAffiliationOptions = networkOptions.map((netOpt) => {
            if (
              p.networkAffiliation &&
              p.networkAffiliation.uuid === netOpt.value
            ) {
              netOpt.isSelected = true
            } else {
              netOpt.isSelected = false
            }
            return netOpt
          })

          p.networkAffiliationOptions = JSON.parse(
            JSON.stringify(networkAffiliationOptions)
          )
          return p
        })
        .sort((a, b) => a.planId.localeCompare(b.planId)),
      disabled: plansGroupedByClientId[clientId].some(
        (plan) =>
          plan.networkAffiliation &&
          plan.networkAffiliation.clientName !== currentClientName
      ),
      isSelected: selectedClientId === clientId
    }))
    .sort((a, b) => a.title.localeCompare(b.title))

  options.unshift({
    title: 'NONE',
    plans: null,
    disabled: false,
    isSelected: false
  })

  return options
}

/**
 * Resolves the clientId (and, on reset, the selectedClient) after the user
 * switches the plan type radio button.
 * @param {string} planType - The newly selected plan type ("Javelina" or "HRP").
 * @param {string} initiallySelectedPlanType - The plan type derived from the client's saved affiliations.
 * @param {Object|null} initiallySelectedClient - The client snapshot derived from the client's saved affiliations.
 * @returns {{clientId: string, selectedClient?: {title: string, value: string}}} The new clientId, and
 *   a reset selectedClient when the chosen plan type differs from the initially selected one.
 */
export const resolvePlanTypeSelection = (planType, initiallySelectedPlanType, initiallySelectedClient) => {
  if (initiallySelectedPlanType === planType) {
    return { clientId: { ...initiallySelectedClient }.title }
  }
  return {
    clientId: 'NONE',
    selectedClient: { title: 'NONE', value: '' }
  }
}

/**
 * Builds the selectedClient object for a newly chosen clientId, pulling its plans
 * from the current client dropdown options.
 * @param {string} clientId - The clientId chosen in the dropdown.
 * @param {Array<Object>} clientOptions - The current client dropdown options.
 * @returns {{title: string, value: string, plans: Array<Object>|undefined}} The new selectedClient.
 */
export const buildSelectedClientFromId = (clientId, clientOptions) => {
  const parsedClientOptions = clientOptions?.find((c) => c.title === clientId)?.plans?.sort((a, b) => a.planId.localeCompare(b.planId))

  return {
    title: clientId,
    value: clientId,
    plans: parsedClientOptions
  }
};

/**
 * Applies a network affiliation selection to the plan matching planId, updating both
 * its networkAffiliationOptions (isSelected flags) and its networkAffiliation field.
 * @param {Array<Object>} plans - The selectedClient's plans.
 * @param {string} planId - The planId whose network affiliation changed.
 * @param {string} networkUuid - The uuid of the newly selected network.
 * @param {string} currentClientName - The clientName of the client currently being edited (from route params).
 * @returns {Array<Object>} The plans with the matching plan's affiliation updated.
 */
export const applyNetworkAffiliationSelection = (plans, planId, networkUuid, currentClientName) =>
  plans.map((p) => {
    if (p.planId === planId) {
      p.networkAffiliationOptions = p.networkAffiliationOptions.map((nao) => {
        if (networkUuid === nao.value) {
          nao.isSelected = true
          p.networkAffiliation = {
            networkName: nao.title,
            uuid: nao.value,
            clientName: currentClientName
          }
        }
        return nao
      })
    }
    return p
  })

/**
 * Computes the key/value pairs present in a but not (identically) present in b.
 * @param {Object} a - The object to diff from.
 * @param {Object} b - The object to diff against.
 * @returns {Object} The pairs of a that differ from b.
 */
export const diffObjects = (a, b) =>
  _.fromPairs(_.differenceWith(_.toPairs(a), _.toPairs(b), _.isEqual))

/**
 * Compares the initially selected client snapshot against the currently selected client,
 * ignoring the derived networkAffiliationOptions field on each plan.
 * @param {Object} initiallySelectedClient - The client snapshot derived from the client's saved affiliations.
 * @param {Object} selectedClient - The client currently selected in the tab.
 * @returns {boolean} True when the two are equivalent once networkAffiliationOptions is ignored.
 */
export const arePlanAffiliationsEqual = (initiallySelectedClient, selectedClient) =>
  _.isEqual(
    { ...initiallySelectedClient, plans: _.map(initiallySelectedClient?.plans, obj => _.omit(obj, ['networkAffiliationOptions'])) },
    { ...selectedClient, plans: _.map(selectedClient?.plans, obj => _.omit(obj, ['networkAffiliationOptions'])) }
  )

/**
 * Derives the clientId and planType implied by the first plan in a list of
 * network-affiliated plans.
 * @param {Array<Object>} plans - Plans with a network affiliation.
 * @returns {{clientId: string, planType: string}|null} The derived selection, or null when plans is empty.
 */
export const resolveClientAndPlanTypeFromPlans = (plans) => {
  const firstPlanInAffiliations = plans?.[0]
  if (!firstPlanInAffiliations) return null

  return {
    clientId: firstPlanInAffiliations?.clientId,
    planType: firstPlanInAffiliations?.planId.startsWith('HRP_') ? 'HRP' : 'Javelina'
  }
}

/**
 * Builds a deep-cloned snapshot of the initially selected client, used later to
 * diff against user edits.
 * @param {string} clientId - The clientId derived from the first affiliated plan.
 * @param {Array<Object>} plans - The network-affiliated plans belonging to that client.
 * @returns {Object} The initiallySelectedClient snapshot.
 */
export const buildInitiallySelectedClientSnapshot = (clientId, plans) =>
  JSON.parse(
    JSON.stringify({
      title: clientId,
      value: clientId,
      plans: plans.sort((a, b) => a.planId.localeCompare(b.planId)),
      disabled: false,
      isSelected: true
    })
  )

/**
 * Builds the payload emitted on the enableSavePlanAffiliation event whenever the
 * selectedClient changes.
 * @param {Object} selectedClient - The client currently selected in the tab.
 * @param {string} planType - The currently selected plan type.
 * @returns {Object} The payload to emit: the selected client plus planType, or a NONE sentinel.
 */
export const buildEnableSavePlanAffiliationPayload = (selectedClient, planType) => {
  if (selectedClient && selectedClient?.plans?.some((p) => p.networkAffiliation)) {
    return { ...selectedClient, planType }
  }
  return { title: 'NONE' }
}

/**
 * Filters dropdown options by the current search text, when text filtering is active.
 * @param {Array<Object>} options - Dropdown options, each with a title.
 * @param {boolean} filterByText - Whether text filtering is currently active.
 * @param {string} searchInputText - The text typed into the dropdown's search input.
 * @returns {Array<Object>} The filtered (or unfiltered) options.
 */
export const filterDropdownOptions = (options, filterByText, searchInputText) => {
  if (filterByText) {
    return options.filter((opt) =>
      opt.title.toLowerCase().includes(searchInputText.toLowerCase())
    )
  }
  return options
}

/**
 * Resolves the text to display in a dropdown's search input.
 * @param {boolean} resetSearchText - Whether the search text should be cleared.
 * @param {string} planType - The planType prop, when the dropdown is used as a plan-type-scoped selector.
 * @param {string} name - The dropdown's name prop.
 * @param {Array<Object>} options - Dropdown options, each optionally flagged with isSelected.
 * @returns {string|undefined} The text to display in the search input.
 */
export const resolveSearchInputText = (resetSearchText, planType, name, options) => {
  if (resetSearchText) return ''
  if (planType) return name
  return findSelectedOption(options)?.title
}

/**
 * Builds the network name dropdown options from a client's network configs.
 * @param {Array<Object>} networkConfigs - The client's draft network configs.
 * @returns {Array<{title: string, value: string}>} The network name options.
 */
export const buildNetworkNameOptions = (networkConfigs) =>
  networkConfigs
    ?.filter((nc) => nc && nc.name)
    .map((nc) => ({ title: nc.name, value: nc.uuid })) ?? []

/**
 * Builds the "client" dropdown option list (used in the parent client-affiliations summary)
 * from a flat list of plans and the currently available network names.
 * @param {Array<Object>} plans - All plans (JV + HRP) for the client's plan types.
 * @param {Array<{title: string, value: string}>} networkNames - Available network name options.
 * @param {string} currentClientName - The clientName of the client currently being edited (from route params).
 * @returns {Array<Object>} Client dropdown options, including a leading "NONE" entry.
 */
export const buildClientsFromPlans = (plans = [], networkNames, currentClientName) => {
  const groupByClientId = groupPlansByClientId(plans)

  const clients = Object.keys(groupByClientId).map((k) => {
    const disabled = groupByClientId[k].some(
      (plan) =>
        plan.networkAffiliation &&
        plan.networkAffiliation.clientName !== currentClientName
    )
    const isSelected = groupByClientId[k].some(
      (plan) =>
        plan.networkAffiliation &&
        networkNames &&
        networkNames.map((nn) => nn.value).includes(plan.networkAffiliation.uuid)
    )

    const oneOfTheSelectedPlan = groupByClientId[k].find(
      (plan) =>
        plan.networkAffiliation &&
        networkNames &&
        networkNames.map((nn) => nn.value).includes(plan.networkAffiliation.uuid)
    )

    const planType = oneOfTheSelectedPlan?.planId.includes('HRP_')
      ? 'HRP'
      : 'Javelina'

    return {
      title: k,
      plans: groupByClientId[k],
      planType,
      disabled,
      isSelected
    }
  })

  clients.sort((a, b) => a.title.localeCompare(b.title))

  clients.unshift({
    title: 'NONE',
    plans: null,
    disabled: false,
    isSelected: false
  })

  return clients
}

/**
 * Extracts the network affiliation uuids present in a list of plans.
 * @param {Array<Object>} plans - Plans to scan.
 * @returns {Array<string>} The uuids of each plan's networkAffiliation, when present.
 */
export const extractNetworkAffiliationUuids = (plans) =>
  plans
    .filter((plan) => plan.networkAffiliation && plan.networkAffiliation.uuid)
    .map((plan) => plan.networkAffiliation.uuid)

/**
 * Decides whether the "save" action should be enabled for a given planAffiliations value.
 * @param {Object|null} planAffiliations - The candidate planAffiliations value.
 * @returns {boolean} True when planAffiliations is present and save should be enabled.
 */
export const resolveSaveEnabledState = (planAffiliations) => !!planAffiliations

/**
 * Builds the request body for the updatePlanNetworkAffiliationForClient API call.
 * @param {Object} planAffiliations - The draft's planAffiliations value (a selectedClient-shaped object).
 * @param {string} clientName - The client's display name.
 * @returns {{clientId: string, clientName: string, planType: string, affiliations: Array<Object>|null, shouldGetHrpPlans: boolean}}
 *   The request body to send to updatePlanNetworkAffiliationForClient.
 */
export const buildAffiliationRequestBody = (planAffiliations, clientName) => {
  const changeAffiliationRequestBody = {
    clientId: planAffiliations?.title,
    clientName,
    planType: planAffiliations?.planType,
    affiliations: [],
    shouldGetHrpPlans: true
  }

  if (planAffiliations?.title !== 'NONE' && planAffiliations?.plans) {
    for (const plan of planAffiliations?.plans?.filter((p) => p.networkAffiliation)) {
      changeAffiliationRequestBody.affiliations.push({
        planId: plan.planId,
        uuid: plan.networkAffiliation.uuid,
        networkName: plan.networkAffiliation.networkName
      })
    }
  } else {
    changeAffiliationRequestBody.affiliations = null
  }

  return changeAffiliationRequestBody
}

/**
 * Splits a list of plans into Javelina and HRP buckets based on the HRP_ planId prefix.
 * @param {Array<Object>} plans - Plans to partition.
 * @returns {{jvPlans: Array<Object>, hrpPlans: Array<Object>}} The partitioned plans.
 */
export const partitionPlansByNamespace = (plans) => ({
  jvPlans: plans.filter((p) => !p.planId.includes('HRP_')),
  hrpPlans: plans.filter((p) => p.planId.includes('HRP_'))
})

/**
 * Merges freshly saved network affiliations into the existing store plans.
 *
 * Note: intentionally preserves a pre-existing bug where the replacement networkAffiliation
 * is always taken from the first entry of updatedPlans with a truthy planId (the inner
 * `find` predicate does not compare against the outer plan's id), rather than the entry
 * matching the current plan. See INT-4034 investigation notes before "fixing" this.
 *
 * @param {Array<Object>} existingPlans - The full existing plans list from the store (jvPlans or hrpPlans).
 * @param {Array<Object>} updatedPlans - The subset of plans whose affiliation changed, from the save response.
 * @returns {Array<Object>} existingPlans with matching entries' networkAffiliation replaced.
 */
export const applyNetworkAffiliationUpdates = (existingPlans, updatedPlans) => {
  const planIdsToUpdate = updatedPlans.map((p) => p.planId)
  return existingPlans.map((p) => {
    if (planIdsToUpdate.includes(p.planId)) {
      p.networkAffiliation = updatedPlans.find((p) => p.planId)?.networkAffiliation
    }
    return p
  })
}
